<script>
	import Button from '$lib/Components/Layout/Button/Button.svelte';
	import Icon from '$lib/Components/Layout/Icon/Icon.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';

	import image1Image from './images/image1.jpg';
	import image2Image from './images/image2.png';
	import crossSectionImagge from './images/IndowPRO-2.0-Cross_Section-tape-callouts.svg';
	import pantoneImage from './images/pantone.svg';
</script>

<Section>
	<p class="text-left">
		Achieve modern energy and acoustic standards in existing buildings without
		the cost, disruption, or aesthetic compromise of window replacement.
		IndowPRO is the engineered secondary glazing system that makes
		high-performance retrofits feasible.
	</p>

	<Image src={image2Image} />

	<p class="text-sm">
		Historic federal office building upgraded with R-16 Vacuum Insulated Glass
		for exceptional energy savings.
	</p>

	<Button
		href="/static/routes/how-to/indowpro-for-architects/IndowPRO_CSI_Spec_-_Section_08_35_13.13_-_Q4_25.pdf"
		>Get the CSI 3-Part Spec (.pdf)</Button
	>
	<Button
		href="/static/routes/how-to/indowpro-for-architects/IW-IndowPRO-Product_Details-Sheet-11-11-25.pdf"
		class="mb-8"
		target="_blank"
		color="white">Download Detail Drawings (.pdf)</Button
	>

	<h2 class="mb-6 text-left">
		The Retrofit Challenge: <span class="text-indow-grey-dark"
			>Performance vs. Irreplaceable Character</span
		>
	</h2>

	<Image src={image1Image} />
	<p class="text-left">
		Project designers are often challenged to improve energy efficiency and meet
		client demands for comfort and quiet, while preserving building aesthetics.
		Full window replacement is often too destructive and expensive, forbidden by
		historic preservation rules, or needlessly damaging to original
		construction. IndowPRO elegantly solves this dilemma.
	</p>

	<h3 class="text-left">Uncompromising Performance</h3>

	<p class="text-left">
		Engineer the building envelope to exceptional standards. The IndowPRO frame
		is a platform for advanced glazings, from Vacuum Insulated Glass (VIG) for
		thermal performance (U-values as low as 0.125) to laminated acoustic glass
		for superior sound attenuation (STC ratings of 50+). All performance data is
		validated through third-party modeling and testing.
	</p>
	<p class="text-left">
		<a href="#downloads">Access all Technical Documents</a>
	</p>

	<Image src={crossSectionImagge} />
	<p>Modular, customizable system</p>

	<h3 class="text-left">CLEAN AESTHETIC SUSTAINS BUILDING INTEGRITY</h3>

	<p class="text-left">
		The IndowPRO system features a clean, low-profile frame and custom Cosmetic
		Cladding that creates a finished, fully-integrated look. Because our system
		is non-destructive and fully reversible, it's ideal for historic
		preservation.
	</p>

	<div class="mb-8 w-2xl rounded-lg bg-indow-grey-dark p-6 shadow-md">
		<Image class="mb-3" src={pantoneImage} />
		<p class="px-3 text-left">
			<strong>Color-Matching to Your Specification.</strong> Frame cladding can be
			matched to any Pantone color to seamlessly blend with existing interior designs.
		</p>
	</div>

	<h3 class="text-left">SEAMLESS PROJECT LOGISTICS</h3>
	<p class="text-left">
		Our system is engineered to solve the most difficult part of retrofits:
		logistics and installation. Frames ship knocked-down for easy site logistics
		and are designed to ensure a reliable, airtight fit even in out-of-square
		openings. The Indow system dramatically reduces measurement errors,
		installation delays, and costly callbacks.
	</p>
	<!-- <Youtube posterImageSrc={thumb_3AD7k9UansQ} videoId="3AD7k9UansQ" /> -->
</Section>

<Section backgroundColor="blue">
	<h2 class="mb-6 text-left text-indow-grey-dark">
		A Smarter Solution for Every Project Goal
	</h2>
	<ul
		class="
    grid w-full list-none gap-6
    md:auto-rows-fr md:grid-cols-3
  "
	>
		<li class="flex h-full flex-col gap-4">
			<p class="mb-0">
				<strong>Acoustic Specs</strong><br />
				The IndowPRO commercial system provides STC 51 and OITC 36 ratings using laminated
				secondary glazing with a decoupled air gap. Technical documents for engineers
				and specifiers are available.
			</p>
			<a
				class="mt-auto flex items-center gap-3 font-medium text-indow-grey-dark"
				href="/how-to/solve-acoustic-issues-in-commercial-buildings"
			>
				View Lab Reports
				<Icon name="arrow" class="w-5" />
			</a>
		</li>
		<li class="flex h-full flex-col gap-4">
			<p class="mb-0">
				<strong>Engineered Reliability</strong><br />
				Enhances asset value with a rapid ROI by significantly cutting utility costs
				and improving tenant retention. All without the high cost and disruption of
				a replacement.
			</p>
			<a
				class="mt-auto flex items-center gap-3 font-medium text-indow-grey-dark"
				href="/how-to/commercial-noi"
			>
				See Performance Data
				<Icon name="arrow" class="w-5" />
			</a>
		</li>
		<li class="flex h-full flex-col gap-4">
			<p class="mb-0">
				<strong>Building Value</strong><br />
				Fast execution and profitable project completion. PRO's knock-down design
				slashes shipping & labor costs, and de-risks jobs by ensuring a reliable fit
				even in out-of-square windows.
			</p>
			<a
				class="mt-auto flex items-center gap-3 font-medium text-indow-grey-dark"
				href="/how-to/install-indowpro"
			>
				Installation Process
				<Icon name="arrow" class="w-5" />
			</a>
		</li>
	</ul>
</Section>
<Section id="downloads" backgroundColor="grey-dark">
	<h2 class="text-left">Your Complete Technical Resource Library</h2>
	<p class="text-left">
		Find all the documentation you need to specify IndowPRO with confidence. All
		files are up-to-date and ready to be incorporated into your project manual.
	</p>

	<Button
		color="blue"
		class="mb-8 place-self-start"
		href="/static/routes/how-to/indowpro-for-architects/IndowPRO_Technical_Library.zip"
		>Download All (.zip)</Button
	>

	<ul
		class="
    grid list-none gap-6 text-indow-blue
    md:grid-cols-2
  "
	>
		<li class="flex items-center gap-2">
			<Icon name="file" class="w-6" /><a
				target="_blank"
				href="/static/routes/how-to/indowpro-for-architects/IndowPRO_CSI_Spec_-_Section_08_35_13.13_-_Q4_25.pdf"
				>CSI 3-Part Specification (.pdf)</a
			>
		</li>
		<li class="flex items-center gap-2">
			<Icon name="file" class="w-6" /><a
				target="_blank"
				href="/static/routes/how-to/indowpro-for-architects/IndowPRO_CSI_Specification_Document_-_Section_08_35_13.13_-_Q4_25.docx"
				>CSI 3-Part Specification (.docx)</a
			>
		</li>
		<li class="flex items-center gap-2">
			<Icon name="file" class="w-6" /><a
				target="_blank"
				href="/static/routes/how-to/indowpro-for-architects/IW-IndowPRO-Product_Data-Sheet-08-10-25.pdf"
				>Product Data Sheet (.pdf)</a
			>
		</li>
		<li class="flex items-center gap-2">
			<Icon name="file" class="w-6" /><a
				target="_blank"
				href="/static/routes/how-to/indowpro-for-architects/IW-IndowPRO-Product_Details-Sheet-11-11-25.pdf"
				>Technical Detail Drawings (Sill, Jamb, Head) (.pdf)</a
			>
		</li>
		<li class="flex items-center gap-2">
			<Icon name="file" class="w-6" /><a
				target="_blank"
				href="/static/routes/how-to/indowpro-for-architects/IndowPRO_Installation_Quick_Start_Guide_Q3_25.pdf"
				>Installation Guide (.pdf)</a
			>
		</li>
	</ul>
</Section>

<Section backgroundColor="blue">
	<h2 class="mb-8 text-left">
		Have a Challenging Project? Let's Talk Details.
	</h2>
	<p class="text-left">
		Our team is ready to partner with you. We can provide project-specific
		recommendations, custom detail drawings, or a virtual "Lunch & Learn" for
		your firm to demonstrate how IndowPRO can help you meet your clients'
		performance goals.
	</p>
	<Button href="/about-indow/commercial-sales"
		>BOOK YOUR CONSULTATION NOW</Button
	>
</Section>
